import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppModal from 'src/components/common/AppModal';
import Utils from 'src/utilities/Functions';
import PrintCodeFetcher from 'src/fetchers/PrintCodeFetcher';

// Component that allows users to print a PDF.
const PrintCodeModal = ({
  element,
  showModal,
  handleModalClose,
  selectedConfig,
  analyses,
}) => {
  // State for the modal and preview
  const [preview, setPreview] = useState(null);
  const [json, setJson] = useState({});

  useEffect(() => {
    // Import the file when the component mounts
    async function loadData() {
      try {
        const response = await fetch('/json/printingConfig/defaultConfig.json');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const tmpJson = await response.json();
        setJson(tmpJson);
      } catch (err) {
        console.error('Failed to fetch JSON', err);
      }
    }
    loadData();
  }, []); // Empty dependency array ensures this runs only once when the component mounts

  const urlError = useMemo(() => {
    const errors = [];
    Object.entries(json).forEach(([key, value]) => {
      if (value != null) {
        if (key === 'code_type' && value !== 'bar_code' && value !== 'qr_code' && value !== 'data_matrix_code') {
          errors.push('Invalid code type. correct values: bar_code, qr_code, data_matrix_code. '
           + 'Value have been overwrite');
        }
        if (key === 'code_image_size' && (value < 0 || value > 100)) {
          errors.push('Invalid code image size, correct values: 0-100. Value  have been overwrite');
        }
        if (key === 'text_position' && value !== 'below' && value !== 'right') {
          errors.push('Invalid text position, correct values: below, right. Value have been overwrite');
        }
      }
    });
    return errors;
  }, [json]);

  // Build the URL when the modal opens
  useEffect(() => {
    if (!showModal) return;

    const ids = analyses.length > 0 ? analyses.map((entry) => entry.id) : [];
    let newUrl = analyses.length > 0
      ? `/api/v1/code_logs/print_analyses_codes?element_type=${element.type}`
        + `&id=${element.id}&analyses_ids[]=${ids}&size=${selectedConfig}`
      : `/api/v1/code_logs/print_codes?element_type=${element.type}&ids[]=${element.id}`;

    if (analyses.length === 0) {
      Object.entries(json).forEach(([configKey, configValue]) => {
        if (configKey === selectedConfig) {
          Object.entries(configValue).forEach(([key, value]) => {
            newUrl += `&${key}=${value}`;
          });
        }
      });
    }
    PrintCodeFetcher.fetchPrintCodes(newUrl).then((result) => {
      if (result != null) setPreview(result);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  // Display the errors in the URL
  const displayUrlErrorMessage = () => {
    if (urlError.length > 0) {
      return (
        <div style={{ color: 'red' }}>
          {urlError.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render the component
  return (
    <AppModal
      title="Print a QR Code/Bar Code"
      size="lg"
      show={showModal}
      onHide={handleModalClose}
      closeLabel="Close"
      primaryActionLabel="Print"
      onPrimaryAction={() => Utils.downloadFile({ contents: preview, name: `print_codes_${element.id}.pdf` })}
    >
      <div>
        <h3>Preview</h3>
        <div>{displayUrlErrorMessage()}</div>
        <div style={{ height: '400px' }}>
          {preview && (
            <embed src={`${preview}#view=FitV`} className="w-100 h-100" />
          )}
        </div>
      </div>
    </AppModal>
  );
}

PrintCodeModal.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  showModal: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  selectedConfig: PropTypes.string.isRequired,
  analyses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  })),
};

PrintCodeModal.defaultProps = {
  analyses: [],
};

export default PrintCodeModal;
